/**
 * Persistencia local (sessionStorage) para marcar errores SIN-ASIGNACION gestionados vía LP.
 *
 * Objetivo:
 * - Reflejar “GESTIONADO (LP)” en el detalle sin re-consultar backend.
 * - Descontar estos casos del contador del resumen (badge por PS) en el rango seleccionado.
 *
 * Nota importante:
 * - Esto NO modifica datos del backend de Gestión de Errores.
 * - Se usa porque el HNP puede seguir existiendo hasta reproceso/procesamiento.
 */

export type LpGestionMarca = {
  referencia_id: string;
  ps_id: number;
  fecha: string; // YYYY-MM-DD
  operario_legajo: number | null;
  creado_en: number; // epoch ms
  modo: 'LP';
};

const STORAGE_KEY_GESTION_LP = 'gestion_errores:gestion_lp:items';

const _safeParseJson = (raw: string | null): any => {
  try {
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const _esISO = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(String(v || '').trim());

const _asNumberOrNull = (v: any): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const txt = String(v ?? '').trim();
  if (!txt) return null;
  if (!/^[0-9]+$/.test(txt)) return null;
  const n = Number(txt);
  return Number.isFinite(n) ? n : null;
};

const _normalizarMarca = (x: any): LpGestionMarca | null => {
  try {
    const referencia_id = String(x?.referencia_id || '').trim();
    const ps_id = _asNumberOrNull(x?.ps_id);
    const fecha = String(x?.fecha || '').trim();
    const operario_legajo = _asNumberOrNull(x?.operario_legajo);
    const creado_en = _asNumberOrNull(x?.creado_en) ?? Date.now();
    const modo = String(x?.modo || '').trim().toUpperCase();

    if (!referencia_id) return null;
    if (ps_id == null) return null;
    if (!_esISO(fecha)) return null;
    if (modo !== 'LP') return null;

    return {
      referencia_id,
      ps_id,
      fecha,
      operario_legajo,
      creado_en,
      modo: 'LP',
    };
  } catch {
    return null;
  }
};

const _leer = (): LpGestionMarca[] => {
  const parsed = _safeParseJson(sessionStorage.getItem(STORAGE_KEY_GESTION_LP));
  if (!Array.isArray(parsed)) return [];

  return parsed
    .map(_normalizarMarca)
    .filter((x): x is LpGestionMarca => !!x);
};

const _guardar = (items: LpGestionMarca[]) => {
  try {
    sessionStorage.setItem(STORAGE_KEY_GESTION_LP, JSON.stringify(items || []));
  } catch {
    // No bloquear si falla el storage.
  }
};

/**
 * Devuelve todas las marcas actualmente guardadas (ya validadas/normalizadas).
 */
export const listarLpGestionados = (): LpGestionMarca[] => {
  // Pequeña limpieza para evitar crecimiento infinito: conservamos ~180 días.
  const items = _leer();
  const limite = Date.now() - 180 * 24 * 60 * 60 * 1000;
  const filtrados = items.filter((x) => (x.creado_en || 0) >= limite);
  if (filtrados.length !== items.length) _guardar(filtrados);
  return filtrados;
};

/**
 * Agrega (o actualiza) una marca por referencia_id.
 */
export const agregarLpGestionado = (marca: Omit<LpGestionMarca, 'creado_en' | 'modo'>): LpGestionMarca => {
  const now = Date.now();
  const normalizada: LpGestionMarca = {
    ...marca,
    creado_en: now,
    modo: 'LP',
  };

  const items = listarLpGestionados();
  const idx = items.findIndex((x) => x.referencia_id === normalizada.referencia_id);
  if (idx >= 0) items[idx] = normalizada;
  else items.push(normalizada);

  _guardar(items);
  return normalizada;
};

export const estaGestionadoPorLP = (referenciaId: string): boolean => {
  const rid = String(referenciaId || '').trim();
  if (!rid) return false;
  return listarLpGestionados().some((x) => x.referencia_id === rid);
};

const _enRango = (fecha: string, desde?: string, hasta?: string): boolean => {
  // Comparación lexicográfica funciona para YYYY-MM-DD.
  if (desde && fecha < desde) return false;
  if (hasta && fecha > hasta) return false;
  return true;
};

/**
 * Cuenta cuántos SIN-ASIGNACION fueron gestionados por LP para un PS dentro del rango.
 *
 * Importante:
 * - Si desde/hasta no están definidos, cuenta todos los guardados para ese PS.
 */
export const contarLpGestionadosParaPs = (psId: number, desde?: string, hasta?: string): number => {
  const id = Number(psId);
  if (!Number.isFinite(id)) return 0;

  const items = listarLpGestionados().filter((x) => x.ps_id === id);
  if (!desde && !hasta) return items.length;
  return items.filter((x) => _enRango(x.fecha, desde, hasta)).length;
};
